/**
 * 權限檢查策略（Policy Object 模式）
 * 所有權限邏輯的單一真相來源（SSOT）
 * 前後端共用此模組進行權限判斷
 */
/**
 * 角色權限等級
 * 用於階層式權限判斷（等級越高，權限越大）
 */
const ROLE_HIERARCHY = {
    OWNER: 3,
    MANAGER: 2,
    STAFF: 1,
};
/**
 * 取得角色等級
 * @param role 角色
 * @returns 角色等級（數字，越大權限越高）
 */
export function getRoleHierarchy(role) {
    return ROLE_HIERARCHY[role];
}
/**
 * 檢查角色等級是否大於等於所需等級
 * @param userRole 用戶角色
 * @param requiredRole 所需角色
 * @returns 是否滿足權限要求
 */
export function hasRoleOrHigher(userRole, requiredRole) {
    if (!userRole) {
        return false;
    }
    return getRoleHierarchy(userRole) >= getRoleHierarchy(requiredRole);
}
/**
 * 檢查是否可以訪問後台管理頁面
 * OWNER 和 MANAGER 可以訪問，STAFF 不能
 * @param role 用戶角色
 * @returns 是否可以訪問後台
 */
export function canAccessAdmin(role) {
    return role === 'OWNER' || role === 'MANAGER';
}
/**
 * 檢查是否可以管理成員（新增、編輯、刪除成員）
 * OWNER 和 MANAGER 可以管理成員
 * @param role 用戶角色
 * @returns 是否可以管理成員
 */
export function canManageMembers(role) {
    return role === 'OWNER' || role === 'MANAGER';
}
/**
 * 檢查是否可以管理員工（新增、編輯、刪除員工）
 * OWNER 和 MANAGER 可以管理員工
 * @param role 用戶角色
 * @returns 是否可以管理員工
 */
export function canManageStaff(role) {
    return role === 'OWNER' || role === 'MANAGER';
}
/**
 * 檢查是否可以刪除 OWNER 角色
 * 任何角色都不能刪除 OWNER（包括 OWNER 自己）
 * @param role 用戶角色
 * @returns 是否可以刪除 OWNER（永遠為 false）
 */
export function canDeleteOwner(_role) {
    return false; // 永遠不能刪除 OWNER
}
/**
 * 檢查是否可以更改角色
 * @param currentRole 當前用戶角色
 * @param targetRole 目標角色
 * @param isChangingSelf 是否在更改自己的角色
 * @returns 是否可以更改角色
 */
export function canChangeRole(currentRole, targetRole, isChangingSelf) {
    if (!currentRole) {
        return false;
    }
    // OWNER 不能把自己的角色改成非 OWNER（每個 tenant 必須有一個且唯一的 OWNER）
    if (isChangingSelf && currentRole === 'OWNER' && targetRole !== 'OWNER') {
        return false;
    }
    // 只有 OWNER 和 MANAGER 可以更改角色
    return canManageMembers(currentRole);
}
/**
 * 檢查是否可以刪除成員
 * @param currentRole 當前用戶角色
 * @param targetRole 目標成員角色
 * @param isDeletingSelf 是否在刪除自己
 * @returns 是否可以刪除成員
 */
export function canDeleteMember(currentRole, targetRole, isDeletingSelf) {
    if (!currentRole) {
        return false;
    }
    // 不能刪除自己
    if (isDeletingSelf) {
        return false;
    }
    // 不能刪除 OWNER
    if (targetRole === 'OWNER') {
        return false;
    }
    // 只有 OWNER 和 MANAGER 可以刪除成員
    return canManageMembers(currentRole);
}
/**
 * 檢查是否可以建立 OWNER 角色（用於邀請或轉移）
 * 只有 OWNER 可以建立新的 OWNER（用於轉移擁有權）
 * @param role 用戶角色
 * @returns 是否可以建立 OWNER
 */
export function canCreateOwner(role) {
    return role === 'OWNER';
}
/**
 * 檢查是否為 OWNER 角色
 * @param role 用戶角色
 * @returns 是否為 OWNER
 */
export function isOwner(role) {
    return role === 'OWNER';
}
/**
 * 檢查是否為 MANAGER 或更高角色
 * @param role 用戶角色
 * @returns 是否為 MANAGER 或 OWNER
 */
export function isManagerOrHigher(role) {
    return role === 'OWNER' || role === 'MANAGER';
}
